import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ViewSetsGroupDropdownItem,
  buildViewsDropdownItem,
} from "./ViewSetsGroupDropdownItem";
import { Accordion, Dropdown, DropdownProps, Input } from "semantic-ui-react";
import { View, ViewSet } from "./types";
import { onKeyUp } from "../../../utils/semanticInputUtils";
import { StyledDropdownBase } from "./ViewSetsCreateNewFilterDropdown";
import { useFavoriteViewMutation } from "../../../api/viewSetApi";
import styled from "styled-components";
import { FontSizeParagraph } from "../top-bar/ProductFilterChoice";

/** Fit current label only — avoid Semantic UI keeping a wider min-width from a previous selection. */
const SavedFiltersDropdown = styled(StyledDropdownBase)`
  &&& {
    width: fit-content !important;
    min-width: 0 !important;
    max-width: 100%;
  }
`;

type Props = {
  activeViewSetId: number;
  viewSetDropdownLabel: string;
  onViewSelected: (selectedView: View) => void;
  toggleActiveViewSetAccordion: (viewSetId: number) => void;
  viewSets: ViewSet[];
  isLoading: boolean;
  showEdited?: boolean;
};
export const ViewSetsGroupContainer: React.FC<Props> = ({
  activeViewSetId,
  viewSetDropdownLabel,
  onViewSelected,
  toggleActiveViewSetAccordion,
  viewSets,
  isLoading,
  showEdited = false,
}) => {
  const [favoriteView] = useFavoriteViewMutation();
  const [searchResult, setSearchResult] = useState<ViewSet[] | []>([]);
  const [open, setOpen] = useState(false);
  const mounted = useRef(true);

  const favoriteViews = useMemo(() => {
    if (!viewSets || isLoading) return [];
    return viewSets
      .flatMap(({ views }) => views.filter(({ favorite }) => favorite))
      .sort((a, b) => {
        if (a.display_name < b.display_name) {
          return -1;
        }
        if (a.display_name > b.display_name) {
          return 1;
        }
        return 0;
      });
  }, [viewSets]);

  useEffect(() => {
    mounted.current = true;
    if (mounted.current) {
      setSearchResult(viewSets);
    }
    return (): void => {
      mounted.current = false;
    };
  }, [viewSets]);

  if (!viewSets || isLoading) {
    return <></>;
  }

  return (
    <div className="tw-flex tw-items-center tw-gap-2">
      <FontSizeParagraph>Saved filters:</FontSizeParagraph>
      <Accordion
        as={SavedFiltersDropdown}
        basic
        button
        className={"tiny"}
        closeOnChange={true}
        onBlur={(): void => setOpen(false)}
        onClick={(): void => setOpen(!open)}
        open={open}
        text={viewSetDropdownLabel}
        scrolling
        data-testid={"filter-group-dropdown"}
      >
        <Dropdown.Menu
          onClick={(e: React.SyntheticEvent): void => e.stopPropagation()}
        >
          <Input
            icon="search"
            iconPosition="left"
            onChange={(
              e: React.SyntheticEvent,
              { value }: DropdownProps
            ): void => {
              setSearchResult(
                viewSets.filter(
                  ({ display_name }) =>
                    display_name
                      .toLowerCase()
                      .search((value as string).toLowerCase()) != -1
                )
              );
            }}
            placeholder={"Search Group"}
            onKeyUp={onKeyUp}
          />

          {!!favoriteViews.length && (
            <>
              <Dropdown.Header>Favorite</Dropdown.Header>
              {buildViewsDropdownItem({
                views: favoriteViews,
                onViewSelected,
                closeByChild: (): void => setOpen(false),
                onFavoriteView: favoriteView,
              })}
            </>
          )}
          <Dropdown.Header>Filters</Dropdown.Header>
          {searchResult.map(({ display_name, id, views }) => (
            <ViewSetsGroupDropdownItem
              activeViewSetId={activeViewSetId}
              closeByChild={(): void => setOpen(false)}
              index={id}
              onViewSelected={onViewSelected}
              key={id}
              title={display_name}
              toggleActiveViewSetAccordion={toggleActiveViewSetAccordion}
              viewSets={views}
              onFavoriteView={favoriteView}
            />
          ))}
        </Dropdown.Menu>
      </Accordion>
      {showEdited && (
        <span className="tw-text-[dimgrey]" data-testid="filter-edited-label">
          Edited
        </span>
      )}
    </div>
  );
};
